import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { plur } from "@/lib/format";
import { toast } from "sonner";
import { Users, Search, X, MoreHorizontal } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { ShiftReviewDialog } from "@/components/shared/ShiftReviewDialog";
import { useIsMobile } from "@/components/shared/PagePrimitives";
import { CrewTimeline, canAdd, findList, fmtHours, hm, num, rowSpan, toMin } from "@/components/diary/CrewTimeline";
import { CrewPanel, ManualPanel } from "@/components/diary/CrewPanels";

// 409 / 418 / 421: parta v deníku (varianta A) — osa je jediný editor. Lidi hledáš v hlavičce karty, pruh party
// tahem změníš (konec = čas a hodiny, střed = posun, šrafy = přestávka), klik otevře panel zprava. Každá změna
// se hned uloží do docházky jako schválená směna (source="diary"), v potvrzení je Zpět. Lidé mimo tým
// (řemeslník, kontrolní den, TDI, BOZP) jsou jen v zápisu deníku — do docházky ani nákladů se nepíšou.
export const crewAutoBreak = (rule, h) => (h > (rule?.min_hours ?? 6) ? (rule?.minutes ?? 30) : 0);
export const fmtH = (h) => String(Math.round(Number(h || 0) * 100) / 100).replace(".", ",");
const pad = (n) => String(n).padStart(2, "0");
const toHHMM = (v) => { const m = /^(\d{1,2}):(\d{2})/.exec(v || ""); return m ? `${pad(+m[1])}:${m[2]}` : "07:00"; };
export function crewEnd(start, hours, brk) {
  const m = /^(\d{1,2}):(\d{2})/.exec(start || "");
  if (!m || !(num(hours) > 0)) return "–";
  const t = +m[1] * 60 + +m[2] + Math.round(num(hours) * 60) + (brk || 0);
  return `${Math.floor(t / 60) % 24}:${pad(t % 60)}`;
}
const fromServer = (rows) => (rows || []).map((r) => ({ user_id: r.user_id, name: r.name, position: r.position, start: toHHMM(r.start),
  hours: r.hours, brk: r.break_auto ? null : r.break_minutes, brkAt: r.break_at || null }));
const typing = (el) => !!el && (/^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName) || el.isContentEditable);

export function DiaryCrew({ projectId, date, manual = [], onManualChange, onSaved, onShiftChanged }) {
  const mobile = useIsMobile();
  const [data, setData] = useState(null);
  const [rows, setRows] = useState([]);
  const [added, setAdded] = useState({});          // user_id → kdy přidán (nejnovější nahoře)
  const [q, setQ] = useState("");
  const [chip, setChip] = useState(null);
  const [panel, setPanel] = useState(null);        // { kind: "row", uid } | { kind: "all" } | { kind: "manual", index, name }
  const [shift, setShift] = useState(null);
  const [flash, setFlash] = useState(null);
  const searchRef = useRef(null);
  const queue = useRef(Promise.resolve());
  const dataRef = useRef(null);
  dataRef.current = data;
  const rule = data?.rule;

  const apply = useCallback((d) => { setData(d); setRows(fromServer(d.rows)); }, []);
  const fail = (e) => setData({ team: [], rows: [], rule: null, error: e?.response?.status === 403 ? formatApiError(e.response?.data?.detail) : "Partu se nepodařilo načíst." });
  useEffect(() => {
    let live = true;
    setData(null); setRows([]); setQ(""); setChip(null); setPanel(null);
    api.get(`/projects/${projectId}/diary/${date}/crew`).then((r) => live && apply(r.data)).catch((e) => live && fail(e));
    return () => { live = false; };
  }, [projectId, date, apply]);
  const reload = async () => {
    try { const { data: d } = await api.get(`/projects/${projectId}/diary/${date}/crew`); apply(d); } catch { /* ignore */ }
    onShiftChanged?.();
  };

  // „/“ skočí do hledání (mimo psaní do pole)
  useEffect(() => {
    const onKey = (e) => { if (e.key === "/" && !typing(e.target) && searchRef.current) { e.preventDefault(); searchRef.current.focus(); } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // ---------- ukládání: hned po změně, postupně za sebou, se Zpět ----------
  const saveRows = (next, msg, prev) => {
    setRows(next);
    queue.current = queue.current.then(async () => {
      try {
        const { data: d } = await api.put(`/projects/${projectId}/diary/${date}/crew`, {
          rows: next.map((r) => ({ user_id: r.user_id, start: toHHMM(r.start), hours: num(r.hours),
            break_minutes: r.brk == null ? null : Math.round(r.brk), break_at: r.brkAt || null })) });
        apply(d);
        if (msg) toast.success(msg, prev ? { action: { label: "Zpět", onClick: () => saveRows(prev, "Vráceno zpět") } } : undefined);
        onSaved?.();
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail));
        setRows(fromServer(dataRef.current?.rows));
      }
    });
    return queue.current;
  };
  const otherSegs = (uid) => ((data?.team || []).find((t) => t.id === uid)?.day || []).filter((s) => s.kind !== "crew")
    .map((s) => ({ a: toMin(s.start), b: s.end ? toMin(s.end) : 24 * 60, project: s.project, hours: s.hours || 0 }));
  const overlaps = (r) => { const sp = r && rowSpan(r, rule); return sp && otherSegs(r.user_id).find((o) => sp.a < o.b && sp.b > o.a); };
  const commit = (uid, patch, msg) => {
    const prev = rows;
    const next = rows.map((r) => (r.user_id === uid ? { ...r, ...patch } : r));
    const hit = overlaps(next.find((r) => r.user_id === uid));
    if (hit) { toast.error(`Překrývá se s ${hit.project} ${hm(hit.a)}–${hm(hit.b)}.`); setRows([...prev]); return; }
    saveRows(next, msg, prev);
  };
  const add = (uid) => {
    const t = (data?.team || []).find((x) => x.id === uid);
    if (!t || !canAdd(t) || rows.some((r) => r.user_id === uid)) return;
    const row = { user_id: uid, name: t.name, position: t.position, start: toHHMM(t.free.start), hours: t.free.hours, brk: null, brkAt: null };
    const sp = rowSpan(row, rule);
    setAdded((a) => ({ ...a, [uid]: Date.now() }));
    setFlash(uid); setTimeout(() => setFlash((f) => (f === uid ? null : f)), 1800);
    setQ(""); setChip(null);
    saveRows([...rows, row], `${t.name} v partě · ${hm(sp.a)}–${hm(sp.b)}`, rows);
  };
  const remove = (uid) => {
    const r = rows.find((x) => x.user_id === uid);
    setPanel(null);
    saveRows(rows.filter((x) => x.user_id !== uid), `${r?.name || "Člověk"} odstraněn z deníku (i z docházky)`, rows);
  };
  const setAll = (vals) => {
    const skip = [];
    const next = rows.map((r) => { const n = { ...r, ...vals }; if (overlaps(n)) { skip.push((r.name || "").split(" ")[0]); return r; } return n; });
    setPanel(null);
    const sp = rowSpan({ start: vals.start, hours: vals.hours, brk: vals.brk }, rule);
    saveRows(next, `Všem v partě ${hm(sp.a)}–${hm(sp.b)}${skip.length ? ` · beze změny ${skip.join(", ")} (byl i jinde)` : ""}`, rows);
  };

  const rowsView = useMemo(() => rows.map((r) => ({ ...r, addedAt: added[r.user_id] || 0 })), [rows, added]);
  const { rest, list } = useMemo(() => findList(data?.team || [], rows, q, chip), [data, rows, q, chip]);
  const onSearchKey = (e) => {
    if (e.key === "Enter") { e.preventDefault(); const f = list.find(canAdd); if (f) add(f.id); }
    else if (e.key === "Escape") { e.preventDefault(); setQ(""); setChip(null); e.currentTarget.blur(); }
  };

  // souhrn v hlavičce: parta + kdo se přihlásil sám na této zakázce
  const team = data?.team || [];
  const hereIds = new Set([...rows.map((r) => r.user_id), ...team.filter((t) => (t.day || []).some((s) => s.kind === "here")).map((t) => t.id)]);
  const hereHours = rows.reduce((s, r) => s + (num(r.hours) || 0), 0)
    + team.reduce((s, t) => s + (t.day || []).filter((x) => x.kind === "here").reduce((a, x) => a + (x.hours || 0), 0), 0);

  if (!data) return null;
  const panelRow = panel?.kind === "row" ? rows.find((r) => r.user_id === panel.uid) : null;
  const manualItem = panel?.kind === "manual" ? (panel.index != null ? manual[panel.index] : { isNew: true, name: panel.name || "" }) : null;
  const saveManual = (w) => {
    const next = panel.index != null ? manual.map((x, i) => (i === panel.index ? w : x)) : [w, ...manual];
    const before = manual;
    const edited = panel.index != null;
    setPanel(null); setQ("");
    onManualChange?.(next);
    toast.success(`${edited ? "Uloženo" : "V deníku"} · ${w.name}${w.start ? ` ${hm(toMin(w.start))}–${hm(toMin(w.end))}` : ""}`,
      { action: { label: "Zpět", onClick: () => onManualChange?.(before) } });
  };
  const removeManual = () => {
    const before = manual, name = manual[panel.index]?.name;
    onManualChange?.(manual.filter((_, i) => i !== panel.index));
    setPanel(null);
    toast.success(`${name} odstraněn z deníku`, { action: { label: "Zpět", onClick: () => onManualChange?.(before) } });
  };

  return (
    <div data-testid="diary-crew">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <h3 className="font-bold flex flex-wrap items-baseline gap-x-2 min-w-0 flex-1 md:mr-auto">
          <span className="inline-flex items-center gap-2"><Users size={18} className="text-brand" /> Pracovníci na stavbě</span>
          {!data.error && <span className="text-[12.5px] font-normal text-muted-foreground tabular-nums" data-testid="crew-tl-sum">
            {hereIds.size ? `${plur(hereIds.size, "člověk", "lidé", "lidí")} · ${fmtHours(hereHours)} na zakázce` : "na zakázce zatím nikdo"}</span>}
        </h3>
        {!data.error && (
          <div className="relative flex items-center w-full md:w-[280px] order-last md:order-none">
            <Search size={15} className="absolute left-3 text-muted-foreground pointer-events-none" aria-hidden="true" />
            <input ref={searchRef} value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onSearchKey} autoComplete="off"
              placeholder={`Najít v týmu… (${rest.length})`} aria-label="Najít člověka v týmu zakázky"
              className="h-10 w-full min-w-0 rounded-full border border-border bg-secondary/60 pl-9 pr-9 text-[15px] focus:outline-none focus:ring-2 focus:ring-brand/50" data-testid="crew-search" />
            {q && <button type="button" onClick={() => setQ("")} aria-label="Smazat hledání" className="absolute right-2 h-7 w-7 grid place-items-center text-muted-foreground"><X size={15} /></button>}
          </div>
        )}
        {!data.error && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button type="button" aria-label="Další volby party" className="h-10 w-10 rounded-full border border-border grid place-items-center hover:bg-secondary shrink-0" data-testid="crew-more"><MoreHorizontal size={17} /></button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[240px]">
              <DropdownMenuItem disabled={!rows.length} onClick={() => setPanel({ kind: "all" })} data-testid="crew-more-all">Všem v partě stejně…</DropdownMenuItem>
              <DropdownMenuItem onClick={() => setPanel({ kind: "manual", index: null, name: "" })} data-testid="crew-more-manual">Přidat člověka mimo tým</DropdownMenuItem>
              <DropdownMenuItem onClick={() => setTimeout(() => searchRef.current?.focus(), 50)}>Najít v týmu <span className="ml-auto text-xs text-muted-foreground">/</span></DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {data.error ? (
        <p className="text-sm border border-amber-500/40 bg-amber-500/10 rounded-[14px] p-3" data-testid="diary-crew-error">{data.error}</p>
      ) : (
        <CrewTimeline team={team} rows={rowsView} manual={manual} rule={rule} today={data.today} now={data.now} mobile={mobile}
          q={q} chip={chip} setChip={setChip} onClear={() => { setQ(""); setChip(null); }} flash={flash}
          onAdd={add} onRowsCommit={commit} onOpenRow={(uid) => setPanel({ kind: "row", uid })} onShift={setShift}
          onOpenManual={(i) => setPanel({ kind: "manual", index: i })} onNewManual={(name) => setPanel({ kind: "manual", index: null, name })} />
      )}
      {!data.error && <p className="mt-2 text-[12px] text-muted-foreground">Změny party se ukládají do docházky hned. Lidé mimo tým jsou jen v zápisu deníku.</p>}

      <CrewPanel open={panel?.kind === "row" && !!panelRow} row={panelRow} rule={rule} others={panelRow ? otherSegs(panelRow.user_id) : []} date={date}
        position={panelRow ? (data?.team || []).find((t) => t.id === panelRow.user_id)?.position : null}
        onClose={() => setPanel(null)} onRemove={() => remove(panel.uid)}
        onSave={(vals) => { const uid = panel.uid, name = panelRow?.name; setPanel(null); commit(uid, vals, `Uloženo · ${name}`); }} />
      <CrewPanel open={panel?.kind === "all"} all count={rows.length} rule={rule} date={date} onClose={() => setPanel(null)} onSave={setAll} />
      <ManualPanel open={panel?.kind === "manual"} item={manualItem} onClose={() => setPanel(null)} onSave={saveManual} onRemove={removeManual} />
      {shift && <ShiftReviewDialog sid={shift} mode="detail" onClose={() => setShift(null)} onChanged={reload} />}
    </div>
  );
}
